import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useEffect } from 'react';

export const initSentry = () => {
  // Only initialize Sentry in production or when explicitly enabled
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.APP_LIC_TYPE || 'development';

  if (!sentryDsn) {
    console.warn('Sentry DSN not configured. Error monitoring is disabled.');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment,
    integrations: [
      ...Sentry.defaultIntegrations.filter(
        (integration) => integration.name !== 'Console'
      ),
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        ),
      }),
      new Sentry.Replay({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: environment === 'PROD' ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking
    release: `erps-portal@${__APP_VERSION__}`,

    // Filter sensitive data
    beforeSend(event) {
      // Don't send events in development unless explicitly enabled
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEBUG) {
        return null;
      }

      // Filter out sensitive information
      if (event.request) {
        delete event.request.cookies;
      }

      // Remove sensitive headers
      if (event.request?.headers) {
        delete event.request.headers['X-Access-Key'];
        delete event.request.headers['Authorization'];
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Network errors
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
      // Browser extensions
      'Extension context invalidated',
      'ResizeObserver loop limit exceeded',
      // Common user actions
      'cancelled',
      'timeout',
    ],
  });
};

// Export for use in components
export { Sentry };
